import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";

import { useUserType } from "../common/UserTypeContext";
import Loader from "../../components/common/Loader";
import { SERVER_ORIGIN } from "../../utilities/constants";
import UserTypeModal from "../../components/common/UserTypeModal";
import css from "../../css/user/cert.module.css";

// Landing UI styling (ported from yi-unified-landing.html)
import "../../css/user/yi-unified-landing.css";
import orgLogo from "../../assets/images/cii_logo.jpg";

const HomePage = () => {
    let { userType } = useUserType();
    const [allVerticals, setAllVerticals] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    const yuvaVerticals = useMemo(() => allVerticals ?? [], [allVerticals]);
    const verticalsCount = yuvaVerticals.length || 9;
    const ecRolesCount = verticalsCount * 2; // Chair + Co-Chair per vertical

    const iconBg = [
        "#FEF0E7",
        "#EEF2FF",
        "#FEF0E7",
        "#F0FDF4",
        "#FFF7ED",
        "#FFF0F0",
        "#EEF2FF",
        "#F0FDF4",
    ];

    // Ensure landing fonts load (only if the app doesn't already include them globally).
    useEffect(() => {
        const FONT_LINK_ID = "yi-landing-fonts";
        if (document.getElementById(FONT_LINK_ID)) return;

        const pre = document.createElement("link");
        pre.id = FONT_LINK_ID;
        pre.rel = "preconnect";
        pre.href = "https://fonts.googleapis.com";
        document.head.appendChild(pre);

        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href =
            "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=DM+Sans:wght@300;400;500;600&display=swap";
        document.head.appendChild(link);
    }, []);

    useEffect(() => {
        setIsLoading(true);

        async function getAllVerticals() {
            try {
                const userId = process.env.REACT_APP_USER_ID;
                const userPassword = process.env.REACT_APP_USER_PASSWORD;
                const basicAuth = btoa(`${userId}:${userPassword}`);

                const response = await fetch(
                    `${SERVER_ORIGIN}/api/student/auth/verticals/all`,
                    {
                        method: "GET",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Basic ${basicAuth}`,
                        },
                    }
                );

                const result = await response.json();

                if (response.status >= 400 && response.status < 600) {
                    if (response.status === 500) {
                        toast.error(result.statusText);
                    }
                } else if (response.ok && response.status === 200) {
                    setAllVerticals(result.allVerticals || []);
                }
            } catch (err) {
                // Swallow error; Loader will stop and empty UI will be shown.
            } finally {
                setIsLoading(false);
            }
        }

        getAllVerticals();
    }, []);

    async function handleViewCourses(verticalId) {
        try {
            const userId = "utkarsh@troology.in";
            const userPassword = "Redhood@23";
            const basicAuth = btoa(`${userId}:${userPassword}`);
            const effectiveUserType = userType || "student";

            const response = await fetch(
                `${SERVER_ORIGIN}/api/${effectiveUserType}/auth/check-authorized`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "auth-token": sessionStorage.getItem("token"),
                        Authorization: `Basic ${basicAuth}`,
                    },
                }
            );

            if (response.status >= 400 && response.status < 600) {
                navigate("/login?userType=student");
            } else if (response.ok && response.status === 200) {
                navigate(`/user/verticals/${verticalId}/courses/all`);
            }
        } catch (error) {
            // no-op
        }
    }

    return isLoading ? (
        <Loader />
    ) : (
        <div className="yi-landing">
            {/* NAV */}
            <nav>
                <div className="nav-logo">

                    <div className="nav-logo-mark">
                        <img
                            src="https://yuvaportal.youngindians.net/static/media/yi_logo.e0c6c2b059a1432f13ef.png"
                            alt="Young Indians Logo"
                            className="nav-logo-img"
                            style={{ width: "34px", height: "34px" }}
                        />
                    </div>
                    <div className="nav-logo-text">
                        Young Indians &nbsp;&nbsp; <span>
                            <img src={orgLogo} draggable="false" alt="org-logo" style={{ width: "65px", height: "33px" }}
                            />
                           
                        </span>
                    </div>
                </div>
                <div className="nav-links">
                    <a href="#about">About Yi</a>
                    <a href="#yuva">YUVA</a>
                    <a href="#thalir">Thalir</a>
                    <a href="#compare">Programmes</a>
                    <UserTypeModal text="Login to Portal" buttonClassName="nav-cta" />
                </div>
            </nav>

            {/* HERO */}
            <section className="hero">
                <div className="hero-bg"></div>
                <div className="hero-grid"></div>
                <div className="hero-content">
                    <div className="hero-badge">
                        <div className="hero-badge-dot"></div>
                        <span>CII Young Indians — Official Platform</span>
                    </div>
                    <div className="hero-tagline">
                        We Can &nbsp;·&nbsp; We Will
                    </div>
                    <h1>
                        Nurturing India's
                        <br />
                        <em>Leaders of Tomorrow</em>
                    </h1>
                    <p>
                        From school classrooms to college campuses — Young Indians runs two integrated
                        programmes to build leadership, entrepreneurship, and civic responsibility in
                        the next generation of Indians.
                    </p>
                    <div className="hero-actions">
                        <a href="#yuva" className="btn-primary">
                            Explore YUVA
                        </a>
                        <a href="#thalir" className="btn-ghost">
                            Discover Thalir
                        </a>
                    </div>
                </div>
                <div className="hero-stats">
                    <div className="hero-stat">
                        <span className="hero-stat-num">76</span>
                        <span className="hero-stat-label">Colleges</span>
                    </div>
                    <div className="hero-stat">
                        <span className="hero-stat-num">
                            8<em>K+</em>
                        </span>
                        <span className="hero-stat-label">YUVA Students</span>
                    </div>
                    <div className="hero-stat">
                        <span className="hero-stat-num">35</span>
                        <span className="hero-stat-label">Schools</span>
                    </div>
                    <div className="hero-stat">
                        <span className="hero-stat-num">
                            13<em>K+</em>
                        </span>
                        <span className="hero-stat-label">Thalir Students</span>
                    </div>
                </div>
            </section>

            {/* ABOUT YI */}
            <section className="about-yi" id="about">
                <div>
                    <div className="section-eyebrow">About the Organisation</div>
                    <h2 className="section-title">The Voice of Young Indians, Globally</h2>
                    <p className="section-body">
                        Young Indians (Yi) is the youth wing of CII — the Confederation of Indian
                        Industry. Founded with a singular mission: harness India's most powerful
                        resource — its youth — and channel that energy into nation-building.
                    </p>
                    <p className="section-body" style={{ marginTop: "16px" }}>
                        Across 66 city chapters and an indirect membership of over 1,00,000 young
                        people, Yi creates platforms for youth to lead, build, and give back — before
                        they've even entered the workforce.
                    </p>
                    <div className="about-pillars">
                        <div className="pillar-card">
                            <div className="pillar-icon">
                                <svg viewBox="0 0 24 24">
                                    <path d="M12 2L3 7l9 5 9-5-9-5z" />
                                    <path d="M3 12l9 5 9-5" />
                                    <path d="M3 17l9 5 9-5" />
                                </svg>
                            </div>
                            <h4>Leadership Development</h4>
                            <p>Students conceptualise and execute real projects in cross-functional teams.</p>
                        </div>
                        <div className="pillar-card">
                            <div className="pillar-icon">
                                <svg viewBox="0 0 24 24">
                                    <circle cx="12" cy="12" r="10" />
                                    <path d="M12 8v4l3 3" />
                                </svg>
                            </div>
                            <h4>Entrepreneurship Mindset</h4>
                            <p>Building the spirit of enterprise and innovation from the ground up.</p>
                        </div>
                        <div className="pillar-card">
                            <div className="pillar-icon">
                                <svg viewBox="0 0 24 24">
                                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                    <circle cx="9" cy="7" r="4" />
                                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                </svg>
                            </div>
                            <h4>Civic Responsibility</h4>
                            <p>Instilling a sense of duty to the nation alongside personal ambition.</p>
                        </div>
                        <div className="pillar-card">
                            <div className="pillar-icon">
                                <svg viewBox="0 0 24 24">
                                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                                </svg>
                            </div>
                            <h4>Measurable Impact</h4>
                            <p>Outcomes tracked across schools, colleges, and regions — not just participation.</p>
                        </div>
                    </div>
                </div>

                <div className="about-visual">
                    <div className="about-card-big">
                        <div className="about-card-num">66</div>
                        <div className="about-card-label">City Chapters Across India</div>
                        <div className="about-card-divider"></div>
                        <div className="about-card-quote">
                            "To strengthen the future of India by giving young people a <strong>stage, a voice, and a vision.</strong>"
                        </div>
                        <div className="about-mini-cards">
                            <div className="mini-card">
                                <span>6,250+</span>
                                <span>Direct Members</span>
                            </div>
                            <div className="mini-card">
                                <span>1L+</span>
                                <span>Indirect Members</span>
                            </div>
                            <div className="mini-card">
                                <span>7</span>
                                <span>Regions</span>
                            </div>
                            <div className="mini-card">
                                <span>2</span>
                                <span>Programmes</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* PROGRAMME INTRO BAND */}
            <div className="programme-band">
                <div className="section-eyebrow" style={{ textAlign: "center" }}>
                    Two Programmes. One Mission.
                </div>
                <h2 className="section-title" style={{ textAlign: "center", margin: "0 auto" }}>
                    From Schools to Colleges — Every Step of the Journey
                </h2>
                <p>Yi runs complementary programmes at each stage of a young person's education.</p>
                <div className="programme-tabs">
                    <a href="#yuva" className="ptab ptab-yuva">YUVA — College Programme</a>
                    <a href="#thalir" className="ptab ptab-thalir">Thalir — School Programme</a>
                </div>
            </div>

            {/* YUVA SECTION */}
            <section className="yuva-section" id="yuva">
                <div className="section-header">
                    <div>
                        <div className="programme-pill pill-yuva">
                            <span className="pill-dot dot-yuva"></span>YUVA Programme
                        </div>
                        <h2 className="section-title">Learning Beyond Education — Harnessing India's Youth Power</h2>
                        <p className="section-body">
                            Yi YUVA empowers passionate college students from across India to take the lead and shape the country's growth. Students work in cross-functional teams on real-world initiatives — conceptualising, planning, and executing projects that create visible social impact.
                        </p>
                        <p className="section-body" style={{ marginTop: "16px" }}>
                            The programme is built around verticals — thematic focus areas where students take up Executive Committee (EC) roles as Chair or Co-Chair, gaining leadership experience that goes well beyond a classroom.
                        </p>
                    </div>
                    <div className="section-header-right">
                        <div style={{ background: "var(--yi-orange-pale)", border: "1px solid rgba(232,93,26,0.2)", borderRadius: "16px", padding: "32px" }}>
                            <div style={{ fontSize: "11px", fontWeight: "700", letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--yi-orange)", marginBottom: "16px" }}>
                                Platform Scale
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                                <div>
                                    <div style={{ fontFamily: "var(--serif)", fontSize: "32px", fontWeight: "700", color: "var(--yi-navy)" }}>76</div>
                                    <div style={{ fontSize: "12px", color: "var(--yi-muted)" }}>Colleges</div>
                                </div>
                                <div>
                                    <div style={{ fontFamily: "var(--serif)", fontSize: "32px", fontWeight: "700", color: "var(--yi-navy)" }}>8,008</div>
                                    <div style={{ fontSize: "12px", color: "var(--yi-muted)" }}>Students</div>
                                </div>
                                <div>
                                    <div style={{ fontFamily: "var(--serif)", fontSize: "32px", fontWeight: "700", color: "var(--yi-navy)" }}>{ecRolesCount}</div>
                                    <div style={{ fontSize: "12px", color: "var(--yi-muted)" }}>EC Roles</div>
                                </div>
                                <div>
                                    <div style={{ fontFamily: "var(--serif)", fontSize: "32px", fontWeight: "700", color: "var(--yi-navy)" }}>{verticalsCount}</div>
                                    <div style={{ fontSize: "12px", color: "var(--yi-muted)" }}>Verticals</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div style={{ marginBottom: "20px" }}>
                    <div className="section-eyebrow">Programme Verticals</div>
                    <div style={{ fontSize: "14px", color: "var(--yi-muted)", marginTop: "4px" }}>
                        Students take on Chair & Co-Chair EC roles across these focus areas
                    </div>
                </div>

                <div className="verticals-grid">
                    {yuvaVerticals.length > 0 ? (
                        yuvaVerticals.map((vertical, idx) => (
                            <div
                                key={vertical._id || idx}
                                className="vertical-card"
                                role="button"
                                tabIndex={0}
                                onClick={() => handleViewCourses(vertical._id)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        handleViewCourses(vertical._id);
                                    }
                                }}
                            >
                                <div className="vertical-icon" style={{ background: iconBg[idx % iconBg.length] }}>
                                    {vertical.imgSrc ? (
                                        <img src={vertical.imgSrc} alt={vertical.name} />
                                    ) : (
                                        <span style={{ fontSize: 18 }}>🌱</span>
                                    )}
                                </div>
                                <h4>{vertical.name}</h4>
                                <p>{vertical.desc}</p>
                                <div className="vertical-meta">
                                    <span>
                                        {typeof vertical.courseCount === "number"
                                            ? `${vertical.courseCount} Courses`
                                            : "Courses Available"}
                                    </span>
                                    {vertical.certUnlocked !== undefined &&
                                        vertical.certUnlocked !== null ? (
                                        <span>{vertical.certUnlocked} Certificates</span>
                                    ) : null}
                                </div>
                            </div>
                        ))
                    ) : (
                        <div style={{ gridColumn: "1 / -1", textAlign: "center", color: "var(--yi-muted)" }}>
                            No verticals found.
                        </div>
                    )}
                </div>
            </section>

            {/* ROLES SECTION */}
            <section className="roles-section">
                <div className="section-eyebrow">How the Platform Works</div>
                <h2 className="section-title">Roles & Hierarchy</h2>
                <p className="section-body">A clearly structured permission model ensures accountability at every level — from national admin to individual student.</p>
                <div className="roles-flow">
                    <div className="role-card">
                        <div className="role-num">1</div>
                        <h4>Admin</h4>
                        <p>National platform management, user oversight, full access</p>
                    </div>
                    <div className="role-arrow">→</div>
                    <div className="role-card">
                        <div className="role-num">2</div>
                        <h4>Regional Mentor</h4>
                        <p>Read-only monitoring across assigned regions</p>
                    </div>
                    <div className="role-arrow">→</div>
                    <div className="role-card">
                        <div className="role-num">3</div>
                        <h4>Chapter EM</h4>
                        <p>Onboards colleges & schools within their chapter</p>
                    </div>
                    <div className="role-arrow">→</div>
                    <div className="role-card">
                        <div className="role-num">4</div>
                        <h4>College Coordinator</h4>
                        <p>Manages students, EC role assignments for their institution</p>
                    </div>
                    <div className="role-arrow">→</div>
                    <div className="role-card">
                        <div className="role-num">5</div>
                        <h4>Students</h4>
                        <p>Access courses, earn certificates, hold EC positions</p>
                    </div>
                </div>
            </section>

            {/* THALIR SECTION */}
            <section className="thalir-section" id="thalir">
                <div className="programme-pill pill-thalir">
                    <span className="pill-dot dot-thalir"></span>Thalir Programme
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "60px", alignItems: "start", marginTop: "20px" }}>
                    <div>
                        <h2 className="section-title">Let's Nurture From School</h2>
                        <p className="section-body">
                            Yi Thalir focuses on the holistic development of school children — working with Indian schools to empower and nurture every child to grow up independent, safe, happy, inclusive, and responsible.
                        </p>
                        <p className="section-body" style={{ marginTop: "16px" }}>
                            Unlike YUVA, Thalir doesn't create individual student accounts. It works at the institution level, with student data captured as aggregate counts — keeping the model simple and scalable for schools of every size.
                        </p>
                        <div style={{ marginTop: "28px", padding: "24px", background: "var(--yi-green-pale)", border: "1px solid rgba(46,125,82,0.2)", borderRadius: "12px" }}>
                            <div style={{ display: "flex", gap: "28px" }}>
                                <div>
                                    <div style={{ fontFamily: "var(--serif)", fontSize: "28px", fontWeight: "700", color: "var(--yi-green)" }}>35</div>
                                    <div style={{ fontSize: "11px", color: "var(--yi-muted)" }}>Schools</div>
                                </div>
                                <div>
                                    <div style={{ fontFamily: "var(--serif)", fontSize: "28px", fontWeight: "700", color: "var(--yi-green)" }}>13,373</div>
                                    <div style={{ fontSize: "11px", color: "var(--yi-muted)" }}>Students</div>
                                </div>
                                <div>
                                    <div style={{ fontFamily: "var(--serif)", fontSize: "28px", fontWeight: "700", color: "var(--yi-green)" }}>1L+</div>
                                    <div style={{ fontSize: "11px", color: "var(--yi-muted)" }}>Indirect Reach</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div>
                        <div className="thalir-focus">
                            <h3>What Thalir Builds in Young Minds</h3>
                            <div className="focus-grid">
                                <div className="focus-item"><div className="focus-item-dot"></div><span>Independence</span></div>
                                <div className="focus-item"><div className="focus-item-dot"></div><span>Safety Awareness</span></div>
                                <div className="focus-item"><div className="focus-item-dot"></div><span>Happiness &amp; Wellbeing</span></div>
                                <div className="focus-item"><div className="focus-item-dot"></div><span>Inclusivity</span></div>
                                <div className="focus-item"><div className="focus-item-dot"></div><span>Civic Responsibility</span></div>
                                <div className="focus-item"><div className="focus-item-dot"></div><span>Leadership Seeds</span></div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="thalir-split">
                    <div className="school-type-card">
                        <div className="stc-header stc-header-single">Single MOU School</div>
                        <div className="stc-body">
                            <div className="stc-row">
                                <div className="stc-dot stc-dot-navy"></div>
                                <p>One <strong>School Coordinator login</strong> per institution.</p>
                            </div>
                            <div className="stc-row">
                                <div className="stc-dot stc-dot-navy"></div>
                                <p>Coordinator manages all student data and programme activities for their school.</p>
                            </div>
                            <div className="stc-row">
                                <div className="stc-dot stc-dot-navy"></div>
                                <p>Student data tracked as <strong>aggregate counts</strong> — no individual student accounts required.</p>
                            </div>
                            <div className="stc-row">
                                <div className="stc-dot stc-dot-navy"></div>
                                <p>Ideal for individual schools signing up directly under a chapter.</p>
                            </div>
                        </div>
                    </div>
                    <div className="school-type-card">
                        <div className="stc-header stc-header-cluster">Cluster MOU School</div>
                        <div className="stc-body">
                            <div className="stc-row">
                                <div className="stc-dot stc-dot-green"></div>
                                <p>A <strong>Nodal Officer</strong> manages multiple smaller schools under one umbrella.</p>
                            </div>
                            <div className="stc-row">
                                <div className="stc-dot stc-dot-green"></div>
                                <p>Bulk student data uploaded via <strong>Excel template</strong> — efficient for large clusters.</p>
                            </div>
                            <div className="stc-row">
                                <div className="stc-dot stc-dot-green"></div>
                                <p>Enables Yi to reach rural and government schools that cannot individually onboard.</p>
                            </div>
                            <div className="stc-row">
                                <div className="stc-dot stc-dot-green"></div>
                                <p>One of the key drivers behind Thalir's 1 lakh+ indirect membership figure.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* COMPARISON SECTION */}
            <section className="compare-section" id="compare">
                <div className="section-eyebrow">Programme Comparison</div>
                <h2 className="section-title">Two Programmes, Designed to Connect</h2>
                <p className="section-body">
                    YUVA and Thalir are not independent — they represent a continuum. A student nurtured through Thalir is better prepared to step into a YUVA leadership role at college.
                </p>
                <div className="compare-grid">
                    <div className="cmp-card cmp-card-yuva">
                        <div className="cmp-logo-area">
                            <span className="cmp-badge cmp-badge-yuva">YUVA</span>
                        </div>
                        <div className="cmp-title">Yi YUVA — College Programme</div>
                        <div className="cmp-sub">Yi Learning Beyond Education</div>
                        <ul className="cmp-rows">
                            <li><span>Target</span>College students across India</li>
                            <li><span>Focus</span>Leadership projects, vertical-based execution, EC roles</li>
                            <li><span>Access</span>Individual student accounts with course &amp; certificate tracking</li>
                            <li><span>Scale</span>76 colleges, 8,008 students active</li>
                            <li><span>Roles</span>Chair + Co-Chair across 9 verticals = 18 EC positions</li>
                            <li><span>Managed by</span>College Coordinator under Chapter EM oversight</li>
                        </ul>
                        <div className="cmp-stat-row">
                            <div className="cmp-stat"><span className="cmp-stat-n">1,222</span><span className="cmp-stat-l">Certs Unlocked</span></div>
                            <div className="cmp-stat"><span className="cmp-stat-n">35</span><span className="cmp-stat-l">Courses Active</span></div>
                        </div>
                    </div>
                    <div className="cmp-card cmp-card-thalir">
                        <div className="cmp-logo-area">
                            <span className="cmp-badge cmp-badge-thalir">THALIR</span>
                        </div>
                        <div className="cmp-title">Yi Thalir — School Programme</div>
                        <div className="cmp-sub">Let's Nurture From School</div>
                        <ul className="cmp-rows">
                            <li><span>Target</span>School students — primary &amp; secondary level</li>
                            <li><span>Focus</span>Holistic development: independence, safety, inclusivity, responsibility</li>
                            <li><span>Access</span>Institution-level; aggregate student counts, no individual accounts</li>
                            <li><span>Scale</span>35 schools, 13,373 students; 1L+ via cluster model</li>
                            <li><span>Roles</span>School Coordinator (Single MOU) or Nodal Officer (Cluster MOU)</li>
                            <li><span>Managed by</span>School or Nodal Officer under Chapter EM oversight</li>
                        </ul>
                        <div className="cmp-stat-row">
                            <div className="cmp-stat"><span className="cmp-stat-n">13,373</span><span className="cmp-stat-l">Students Tracked</span></div>
                            <div className="cmp-stat"><span className="cmp-stat-n">2</span><span className="cmp-stat-l">School Types</span></div>
                        </div>
                    </div>
                </div>
            </section>

            {/* JOURNEY SECTION */}
            <section className="journey-section">
                <div className="section-eyebrow" style={{ textAlign: "center" }}>The Continuum</div>
                <h2 className="section-title" style={{ textAlign: "center", maxWidth: "500px", margin: "0 auto" }}>
                    A Young Indian's Journey Through Yi
                </h2>
                <div className="journey-line">
                    <div className="journey-step">
                        <div className="journey-dot dot-school">🌱</div>
                        <div className="journey-content">
                            <h4>School Years — Thalir</h4>
                            <p>
                                A child's values, habits, and sense of responsibility are shaped early. Thalir plants the seeds — <em>independence, inclusivity, civic pride</em> — before formal career or leadership thinking begins.
                            </p>
                        </div>
                    </div>
                    <div className="journey-step">
                        <div className="journey-dot dot-college">🎓</div>
                        <div className="journey-content">
                            <h4>College Years — YUVA</h4>
                            <p>
                                The Thalir alumni arrives at college already primed. YUVA gives them a <em>cross-functional team, a real vertical, an EC role</em> — and the responsibility to conceptualise, plan, and execute work that matters.
                            </p>
                        </div>
                    </div>
                    <div className="journey-step">
                        <div className="journey-dot dot-nation">🇮🇳</div>
                        <div className="journey-content">
                            <h4>Workforce &amp; Beyond — Yi Members</h4>
                            <p>
                                The YUVA alumnus enters professional life with leadership credentials, a network of 6,250+ Yi members, and a demonstrated record of impact. <em>The voice of Young Indians, globally.</em>
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="cta-section">
                <div className="section-eyebrow" style={{ textAlign: "center" }}>Get Involved</div>
                <h2 className="section-title" style={{ maxWidth: "520px" }}>Be Part of the Movement That Shapes India</h2>
                <p className="section-body" style={{ maxWidth: "500px" }}>
                    Whether you're a college, a school, a student, or an educator — there's a place for you in the Young Indians ecosystem.
                </p>
                <div className="cta-btns">
                    <a href="https://yuvaportal.youngindians.net/yuva-login" className="btn-primary">Access YUVA Portal</a>
                    <a href="https://yuvaportal.youngindians.net/thalir-login" className="btn-green">Learn About Thalir</a>
                    <a
                        href="https://youngindians.net/join-movement"
                        className="btn-ghost"
                        style={{ color: "rgba(255,255,255,0.6)", borderColor: "rgba(255,255,255,0.15)" }}
                    >
                        Join the Movement
                    </a>
                </div>
            </section>

            {/* FOOTER */}
            <footer>
                <div className="footer-left">
                    <div className="footer-logomark">  <img
                        src="https://yuvaportal.youngindians.net/static/media/yi_logo.e0c6c2b059a1432f13ef.png"
                        alt="Young Indians Logo"
                        className="nav-logo-img"
                        style={{ width: "32px", height: "32px" }}
                    /></div>
                    <span>Young Indians — A CII Initiative &nbsp;·&nbsp; © 2024</span>
                </div>
                <div className="footer-links">
                    <a href="https://youngindians.net/about-us/">About Yi</a>
                    <a href="https://youngindians.net/yuva/">YUVA</a>
                    <a href="https://youngindians.net/yi-thalir/">Thalir</a>
                    <a href="#">Privacy Policy</a>
                    <a href="#">Terms &amp; Conditions</a>
                    <a href="https://youngindians.net/contact-us/">Contact</a>
                </div>
                <div className="footer-right">Developed by Troology Technologies</div>
            </footer>
        </div>
    );
};

export default HomePage;

